import { Autocomplete, AutocompleteInputChangeReason, AutocompleteRenderInputParams, TextField } from '@mui/material';
import { HTMLAttributes, ReactElement, ReactNode, Ref, SyntheticEvent, useCallback, useState } from 'react';
import { BillingPayerOption } from 'utils/lib/types/data/billing/billing.types';
import { searchBillingCustomInsuranceOrgs, searchBillingPayers } from '../api/api';
import { useApiClients } from '../hooks/useAppClients';
import { useDebounce } from '../hooks/useDebounce';

// Searchable payer picker backed by both the Oystehr RCM payer list (search-billing-payers) and the
// billing app's own custom insurance organizations (search-billing-custom-insurance-orgs). It displays
// the human-friendly payer name + id, but stores the payer Organization's id — the same token
// coverage.payor / Claim.insurer use (see getPayerUrl/extractPayerIdFromUrl/buildPayorReference), so it
// round-trips with the rules engine's payerId reader/writer. Selecting only valid payers (no free
// text) is the point.

interface PayerSelectProps {
  multiple: boolean;
  value: string | string[] | null | undefined;
  onChange: (value: string | string[]) => void;
  label?: string;
  // Options the caller already knows (e.g. the claim's current payer), so a stored id shows its
  // display name before any search has run.
  initialOptions?: BillingPayerOption[];
  required?: boolean;
  // Validation display + react-hook-form focus ref, for use inside Controller-registered forms.
  error?: boolean;
  helperText?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  fullWidth?: boolean;
}

// a known payer whose clearinghouse id isn't loaded yet shows by name alone
const optionLabel = (o: BillingPayerOption): string =>
  o.name ? (o.payerId ? `${o.name} (${o.payerId})` : o.name) : o.id;

// Debounced server-side search (RCM payers + custom insurance organizations, merged) plus a memory of
// payers we've seen, so a selected payer keeps its label even after the option list changes (or on
// edit, once it shows up in a search). Exported so callers with their own payer Autocomplete (e.g. the
// claims/ERA list filters) can share the same fetch/merge logic instead of duplicating it. `search`'s
// identity is stable (memoized on `oystehrZambda` only) so callers can safely put it in a `useEffect`
// dependency array to trigger an initial load.
export function usePayerSearch(initialOptions?: BillingPayerOption[]): {
  options: BillingPayerOption[];
  known: Record<string, BillingPayerOption>;
  search: (query?: string) => void;
} {
  const { oystehrZambda } = useApiClients();
  const { debounce } = useDebounce(300);
  const [options, setOptions] = useState<BillingPayerOption[]>([]);
  const [known, setKnown] = useState<Record<string, BillingPayerOption>>(() =>
    Object.fromEntries((initialOptions ?? []).filter((o) => o.id).map((o) => [o.id, o]))
  );

  const search = useCallback(
    (query?: string): void => {
      if (!oystehrZambda) return;
      debounce(() => {
        void (async (): Promise<void> => {
          const [payersRes, customRes] = await Promise.allSettled([
            searchBillingPayers(oystehrZambda, query ? { name: query } : {}),
            searchBillingCustomInsuranceOrgs(oystehrZambda, query ? { name: query } : {}),
          ]);
          const payers = payersRes.status === 'fulfilled' ? payersRes.value.payers ?? [] : [];
          const customOrgs = customRes.status === 'fulfilled' ? customRes.value.organizations ?? [] : [];
          const customOptions: BillingPayerOption[] = customOrgs.map((o) => ({
            id: o.id,
            name: o.name,
            payerId: o.orgId,
          }));
          const merged = [...customOptions, ...payers];
          setOptions(merged);
          setKnown((prev) => {
            const next = { ...prev };
            merged.forEach((p) => (next[p.id] = p));
            return next;
          });
        })();
      });
    },
    [oystehrZambda, debounce]
  );

  return { options, known, search };
}

// Resolve a stored id to a display option, falling back to a synthetic option showing the raw id.
const resolve = (
  id: string,
  known: Record<string, BillingPayerOption>,
  options: BillingPayerOption[]
): BillingPayerOption => known[id] ?? options.find((o) => o.id === id) ?? { id, name: '', payerId: id };

export function PayerSelect({
  multiple,
  value,
  onChange,
  label = 'Payer',
  initialOptions,
  required,
  error,
  helperText,
  inputRef,
  fullWidth,
}: PayerSelectProps): ReactElement {
  const { options, known, search } = usePayerSearch(initialOptions);

  // Props shared by the single- and multi-select variants. Callbacks are typed with their own
  // (narrower) signatures so the object is assignable to both Autocomplete generic instantiations.
  const shared = {
    size: 'small' as const,
    fullWidth,
    filterOptions: (x: BillingPayerOption[]): BillingPayerOption[] => x,
    isOptionEqualToValue: (o: BillingPayerOption, v: BillingPayerOption): boolean => o.id === v.id,
    getOptionLabel: optionLabel,
    renderOption: (props: HTMLAttributes<HTMLLIElement>, o: BillingPayerOption): ReactElement => (
      <li {...props} key={o.id}>
        {optionLabel(o)}
      </li>
    ),
    onOpen: () => search(),
    onInputChange: (_: SyntheticEvent, v: string, reason: AutocompleteInputChangeReason): void => {
      if (reason === 'input') search(v || undefined);
    },
    renderInput: (params: AutocompleteRenderInputParams): ReactElement => (
      <TextField
        {...params}
        label={label}
        placeholder="Search payers…"
        required={required}
        error={error}
        helperText={helperText}
        inputRef={inputRef}
      />
    ),
  };

  if (multiple) {
    const ids = Array.isArray(value) ? value : value ? [value] : [];
    const selected = ids.map((id) => resolve(id, known, options));
    const merged = [...selected.filter((s) => !options.some((o) => o.id === s.id)), ...options];
    return (
      <Autocomplete<BillingPayerOption, true, false, false>
        {...shared}
        multiple
        options={merged}
        value={selected}
        onChange={(_, opts) => onChange(opts.map((o) => o.id))}
        sx={{ minWidth: 260 }}
      />
    );
  }

  const id = typeof value === 'string' ? value : '';
  const selected = id ? resolve(id, known, options) : null;
  const merged = selected && !options.some((o) => o.id === selected.id) ? [selected, ...options] : options;
  return (
    <Autocomplete<BillingPayerOption, false, false, false>
      {...shared}
      options={merged}
      value={selected}
      onChange={(_, o) => onChange(o?.id ?? '')}
      sx={{ minWidth: 240 }}
    />
  );
}
